// Owner: admin-ops (settings cleanup). The optional workspace modules in the app: `me.modules` (Settings › General ›
// Modules). A module that is off has no rail item, no ⌘K commands, no entry points on records, Home or the Inbox, and
// its routes show a "turned off" state; the API refuses its routes anyway (409 `module_off`).
//
// Read from the cached `/me` of the app's query client, so it works in React (`useModuleOn`) and outside it
// (`moduleOn` in contribution checks and command providers). Until `/me` is known (public pages, unit tests of
// screens) nothing is hidden.
import { useSyncExternalStore } from 'react';
import { type MeDTO, WORKSPACE_MODULES, type WorkspaceModule } from '@dataroom/core';
import { qk, queryClient } from './queries';

export type { WorkspaceModule };

/** The modules of the cached `/me`, as one comparable string (null: not known yet). */
function snapshot(): string | null {
  const me = queryClient.getQueryData<MeDTO>(qk.me);
  return me ? (me.modules ?? []).join(',') : null;
}

function subscribe(onChange: () => void): () => void {
  return queryClient.getQueryCache().subscribe((event) => {
    if (event.query.queryKey[0] === qk.me[0]) onChange();
  });
}

function includes(state: string | null, module: WorkspaceModule): boolean {
  return state === null || state.split(',').includes(module);
}

/** Whether a module is on (outside React: contribution checks, command providers). */
export function moduleOn(module: WorkspaceModule): boolean {
  return includes(snapshot(), module);
}

/** Whether a module is on; re-renders when an admin turns it on or off. */
export function useModuleOn(module: WorkspaceModule): boolean {
  return includes(useSyncExternalStore(subscribe, snapshot, snapshot), module);
}

/** The modules that are on (canonical order); null while `/me` is not known. */
export function useModules(): readonly WorkspaceModule[] | null {
  const state = useSyncExternalStore(subscribe, snapshot, snapshot);
  return state === null ? null : WORKSPACE_MODULES.filter((m) => state.split(',').includes(m));
}
